import { Fragment, useEffect, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const months = ["January","February","March","April","May","June",
 "July","August","September","October","November","December"];

const paymentModes = ["NEFT", "RTGS", "Cheque", "Cash", "UPI", "Other"];

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const SalaryAccounts = () => {
 const [month, setMonth] = usePersistentState("salary_month", new Date().getMonth());
 const [year, setYear] = usePersistentState("salary_year", new Date().getFullYear());
 const [search, setSearch] = usePersistentState("salary_search", "");
 const [statusFilter, setStatusFilter] = usePersistentState("salary_status", "");
 const [records, setRecords] = useState([]);
 const [summary, setSummary] = useState({});
 const [loading, setLoading] = useState(false);
 const [downloadingId, setDownloadingId] = useState(null);

 const [showPayModal, setShowPayModal] = useState(false);
 const [selectedRec, setSelectedRec] = useState(null);
 const [paying, setPaying] = useState(false);
 const [payForm, setPayForm] = useState({
 paidAmount: "", paymentMode: "NEFT", paymentDate: "", paymentRemarks: "",
 });

 useEffect(() => { loadRecords(); }, [month, year, statusFilter]);

 const loadRecords = async () => {
 setLoading(true);
 try {
 const res = await api.get("/staff-payroll", { params: { month, year, status: statusFilter } });
 setRecords(res.data.data || []);
 setSummary(res.data.summary || {});
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to load", "error");
 }
 setLoading(false);
 };

 const filtered = records.filter((r) => {
 const q = search.toLowerCase();
 return !q || (r.employeeName || "").toLowerCase().includes(q);
 });

 // Mark Paid 
 const openPay = (rec) => {
 setSelectedRec(rec);
 setPayForm({
 paidAmount: String(rec.netPay || ""),
 paymentMode: "NEFT",
 paymentDate: new Date().toISOString().split("T")[0],
 paymentRemarks: "",
 });
 setShowPayModal(true);
 };

 const handleMarkPaid = async () => {
 if (!selectedRec) return;
 setPaying(true);
 try {
 await api.patch(`/staff-payroll/${selectedRec._id}/pay`, {
 paidAmount: Number(payForm.paidAmount),
 paymentMode: payForm.paymentMode,
 paymentDate: payForm.paymentDate,
 paymentRemarks: payForm.paymentRemarks,
 });
 swal("Paid!", "Salary marked as paid successfully", "success");
 setShowPayModal(false);
 loadRecords();
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to mark paid", "error");
 }
 setPaying(false);
 };

 // Download PDF Slip 
 const downloadSlip = async (rec) => {
 setDownloadingId(rec._id);
 try {
 const token = localStorage.getItem("token") ||
 (() => {
 try { return JSON.parse(localStorage.getItem("userDetails") || "{}").token; }
 catch { return ""; }
 })();

 const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
 const url = `${baseUrl}/api/staff-payroll/${rec._id}/slip`;

 const response = await fetch(url, {
 headers: { Authorization: `Bearer ${token}` },
 });

 if (!response.ok) {
 let errMsg = "Failed to generate salary slip";
 try { const j = await response.json(); errMsg = j.message || errMsg; } catch { /* ignore */ }
 swal("Error", errMsg, "error");
 setDownloadingId(null);
 return;
 }

 const contentType = response.headers.get("content-type") || "";
 if (!contentType.includes("pdf")) {
 let errMsg = "Server returned unexpected response";
 try { const txt = await response.text(); const j = JSON.parse(txt); errMsg = j.message || errMsg; } catch { /* ignore */ }
 swal("Error", errMsg, "error");
 setDownloadingId(null);
 return;
 }

 const arrayBuffer = await response.arrayBuffer();
 const bytes = new Uint8Array(arrayBuffer.slice(0, 5));
 console.log("[slip] First bytes:", Array.from(bytes).map(b => b.toString(16).padStart(2,"0")).join(" "), "| size:", arrayBuffer.byteLength);

 const blob = new Blob([arrayBuffer], { type: "application/pdf" });
 const objectUrl = URL.createObjectURL(blob);
 const link = document.createElement("a");
 link.href = objectUrl;
 link.download = `SalarySlip_${(rec.employeeName || "staff").replace(/\s+/g, "_")}_${months[rec.month]}_${rec.year}.pdf`;
 document.body.appendChild(link);
 link.click();
 link.remove();
 setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
 } catch (err) {
 swal("Error", err.message || "Failed to generate salary slip", "error");
 }
 setDownloadingId(null);
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Salary Accounts" motherMenu="HR" />

 {/* Summary Cards */}
 <Row className="g-3 mb-3">
 <Col md={3}>
 <Card className="h-100"><Card.Body>
 <div className="text-muted small">Total Processed</div>
 <h4 className="mb-0">{summary.total || 0}</h4>
 </Card.Body></Card>
 </Col>
 <Col md={3}>
 <Card className="h-100"><Card.Body>
 <div className="text-muted small">Pending</div>
 <h4 className="mb-0 text-warning">{summary.pending || 0}</h4>
 <div className="text-muted small">₹{fmt(summary.totalPending)}</div>
 </Card.Body></Card>
 </Col>
 <Col md={3}>
 <Card className="h-100"><Card.Body>
 <div className="text-muted small">Paid</div>
 <h4 className="mb-0 text-success">{summary.paid || 0}</h4>
 <div className="text-muted small">₹{fmt(summary.totalPaid)}</div>
 </Card.Body></Card>
 </Col>
 <Col md={3}>
 <Card className="h-100"><Card.Body>
 <div className="text-muted small">Total Net Pay</div>
 <h4 className="mb-0">₹{fmt(summary.totalNetPay)}</h4>
 </Card.Body></Card>
 </Col>
 </Row>

 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title className="mb-0">Salary Accounts — {months[month]} {year}</Card.Title>
 </Card.Header>
 <Card.Body>
 <Row className="g-2 mb-3">
 <Col md={2}>
 <Form.Label>Month</Form.Label>
 <Form.Select size="sm" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
 {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label>Year</Form.Label>
 <Form.Select size="sm" value={year} onChange={(e) => setYear(Number(e.target.value))}>
 {[2024,2025,2026,2027,2028].map((y) => <option key={y}>{y}</option>)}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label>Status</Form.Label>
 <Form.Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
 <option value="">All</option>
 <option value="PENDING">Pending</option>
 <option value="PAID">Paid</option>
 </Form.Select>
 </Col>
 <Col md={4}>
 <Form.Label>Search</Form.Label>
 <Form.Control size="sm" placeholder="Search name..." value={search}
 onChange={(e) => setSearch(e.target.value)} />
 </Col>
 </Row>

 {loading ? (
 <div className="text-center py-5"><Spinner /></div>
 ) : (
 <Table bordered hover responsive size="sm">
 <thead className="bg-light">
 <tr>
 <th>#</th>
 <th>Employee</th>
 <th>Days</th>
 <th>Gross</th>
 <th>Deduction</th>
 <th>Net Pay</th>
 <th>Status</th>
 <th>Paid On</th>
 <th>Mode</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {filtered.length === 0 ? (
 <tr>
 <td colSpan={10} className="text-center text-muted py-4">
 No processed salaries found for this month
 </td>
 </tr>
 ) : filtered.map((r, i) => (
 <tr key={r._id}>
 <td>{i + 1}</td>
 <td>
 <strong>{r.employeeName}</strong>
 <div className="text-muted small">{r.employeeEmail}</div>
 {r.uan && <div className="text-muted small">UAN: {r.uan}</div>}
 </td>
 <td className="text-center">{r.presentDays}</td>
 <td>₹{fmt(r.gross)}</td>
 <td className="text-danger">₹{fmt(r.totalDeduction)}</td>
 <td className="fw-bold">₹{fmt(r.netPay)}</td>
 <td>
 {r.status === "PAID"
 ? <Badge bg="success">Paid</Badge>
 : <Badge bg="warning" text="dark">Pending</Badge>}
 </td>
 <td>{r.paymentDate ? new Date(r.paymentDate).toLocaleDateString("en-IN") : "-"}</td>
 <td>{r.paymentMode || "-"}</td>
 <td>
 <div className="d-flex gap-1 flex-wrap">
 <Button
 size="sm"
 variant="outline-primary"
 disabled={downloadingId === r._id}
 onClick={() => downloadSlip(r)}
 >
 {downloadingId === r._id
 ? <><Spinner size="sm" className="me-1" />Generating...</>
 : <><i className="fas fa-download me-1" />Slip</>}
 </Button>
 {r.status === "PENDING" && (
 <Button size="sm" variant="success" onClick={() => openPay(r)}>
 Mark Paid
 </Button>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Card.Body>
 </Card>

 {/* Mark Paid Modal */}
 <Modal show={showPayModal} onHide={() => setShowPayModal(false)} centered>
 <Modal.Header closeButton>
 <Modal.Title>Mark Salary Paid — {selectedRec?.employeeName}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <div className="mb-3 p-3 bg-light rounded">
 <div>Net Pay: <strong>₹{fmt(selectedRec?.netPay)}</strong></div>
 <div className="text-muted small">
 Gross ₹{fmt(selectedRec?.gross)} | Deduction ₹{fmt(selectedRec?.totalDeduction)}
 </div>
 </div>
 <Row className="g-2">
 <Col md={6}>
 <Form.Label>Paid Amount</Form.Label>
 <Form.Control type="number" value={payForm.paidAmount}
 onChange={(e) => setPayForm({ ...payForm, paidAmount: e.target.value })} />
 </Col>
 <Col md={6}>
 <Form.Label>Payment Mode</Form.Label>
 <Form.Select value={payForm.paymentMode}
 onChange={(e) => setPayForm({ ...payForm, paymentMode: e.target.value })}>
 {paymentModes.map((m) => <option key={m}>{m}</option>)}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label>Payment Date</Form.Label>
 <Form.Control type="date" value={payForm.paymentDate}
 onChange={(e) => setPayForm({ ...payForm, paymentDate: e.target.value })} />
 </Col>
 <Col md={12}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control placeholder="Optional remarks" value={payForm.paymentRemarks}
 onChange={(e) => setPayForm({ ...payForm, paymentRemarks: e.target.value })} />
 </Col>
 </Row>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowPayModal(false)}>Cancel</Button>
 <Button variant="success" disabled={paying} onClick={handleMarkPaid}>
 {paying ? "Saving..." : "Confirm Paid"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default SalaryAccounts;
